import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  TextField,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  Divider,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';

// Status chip colors
const STATUS_COLORS = {
  'Under Consideration': 'info',
  'Approved': 'success',
  'In Development': 'warning',
  'Released': 'primary',
  'Declined': 'error'
};

const FeatureRequestPage = ({ user }) => {
  const navigate = useNavigate();
  const [featureRequests, setFeatureRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [newRequest, setNewRequest] = useState({ title: '', description: '' });
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Status options for admin
  const statusOptions = [
    'Under Consideration',
    'Approved',
    'In Development',
    'Released',
    'Declined',
    { value: '', label: 'Remove Status' }
  ];

  // Load feature requests from Firebase
  useEffect(() => {
    const fetchFeatureRequests = async () => {
      try {
        const q = query(collection(db, 'featureRequests'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const requests = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
        }));
        
        setFeatureRequests(requests);
      } catch (error) {
        console.error('Error fetching feature requests:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatureRequests();
  }, []);

  const handleOpenDialog = (request = null) => {
    if (request) {
      setEditingRequest(request);
      setNewRequest({ 
        title: request.title, 
        description: request.description 
      });
    } else {
      setEditingRequest(null);
      setNewRequest({ title: '', description: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewRequest({ title: '', description: '' });
    setEditingRequest(null);
  };

  const handleSubmitRequest = async () => {
    if (!newRequest.title || !newRequest.description) return;
    
    setSubmitting(true);
    
    try {
      if (editingRequest) {
        // Update existing request
        const requestRef = doc(db, 'featureRequests', editingRequest.id);
        await updateDoc(requestRef, {
          title: newRequest.title,
          description: newRequest.description
        });
        
        // Update local state
        setFeatureRequests(featureRequests.map(request => 
          request.id === editingRequest.id 
            ? { 
                ...request, 
                title: newRequest.title, 
                description: newRequest.description 
              } 
            : request
        ));
      } else {
        // Create new feature request in Firebase
        const docRef = await addDoc(collection(db, 'featureRequests'), {
          title: newRequest.title,
          description: newRequest.description,
          status: '',
          createdBy: {
            id: user?.uid || 'anonymous',
            name: user?.displayName || 'Anonymous User',
            avatar: user?.photoURL
          },
          createdAt: serverTimestamp(),
          upvotes: 0,
          upvotedBy: []
        });
        
        // Add to local state
        const newFeatureRequest = {
          id: docRef.id,
          title: newRequest.title,
          description: newRequest.description,
          status: '',
          createdBy: {
            id: user?.uid || 'anonymous',
            name: user?.displayName || 'Anonymous User',
            avatar: user?.photoURL
          },
          createdAt: new Date(),
          upvotes: 0,
          upvotedBy: []
        };
        
        setFeatureRequests([newFeatureRequest, ...featureRequests]);
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving feature request:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleActionClick = (event, request) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
  };

  const openDeleteConfirmation = () => {
    setActionAnchorEl(null);
    setConfirmDelete(true);
  };

  const handleDeleteRequest = async () => {
    if (!selectedRequest) return;
    
    try {
      // Delete from Firebase
      const requestRef = doc(db, 'featureRequests', selectedRequest.id);
      await deleteDoc(requestRef);
      
      // Remove from local state
      setFeatureRequests(featureRequests.filter(request => request.id !== selectedRequest.id));
      console.log('Feature request deleted successfully');
    } catch (error) {
      console.error('Error deleting feature request:', error);
    } finally {
      setConfirmDelete(false);
      setSelectedRequest(null);
    }
  };

  const handleUpvote = async (requestId) => {
    try {
      const requestRef = doc(db, 'featureRequests', requestId);
      const requestToUpdate = featureRequests.find(req => req.id === requestId);
      
      if (!requestToUpdate) return;
      
      const userId = user?.uid || 'anonymous';
      const alreadyUpvoted = requestToUpdate.upvotedBy?.includes(userId);
      
      const updatedUpvotedBy = alreadyUpvoted 
        ? (requestToUpdate.upvotedBy || []).filter(id => id !== userId)
        : [...(requestToUpdate.upvotedBy || []), userId];
        
      const updatedUpvotes = alreadyUpvoted 
        ? Math.max(0, (requestToUpdate.upvotes || 0) - 1)
        : (requestToUpdate.upvotes || 0) + 1;
      
      // Update in Firebase
      await updateDoc(requestRef, {
        upvotes: updatedUpvotes,
        upvotedBy: updatedUpvotedBy
      });
      
      // Update local state
      setFeatureRequests(featureRequests.map(request => 
        request.id === requestId 
          ? { 
              ...request, 
              upvotes: updatedUpvotes,
              upvotedBy: updatedUpvotedBy
            } 
          : request
      ));
    } catch (error) {
      console.error('Error updating upvote:', error);
    }
  };

  const handleStatusClick = (event, request) => {
    setStatusAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  };

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
    setSelectedRequest(null);
  };

  const handleStatusChange = async (status) => {
    if (!selectedRequest) {
      handleStatusClose();
      return;
    }
    
    try {
      const requestRef = doc(db, 'featureRequests', selectedRequest.id);
      
      // Update in Firebase
      await updateDoc(requestRef, { status });
      
      // Update local state
      setFeatureRequests(featureRequests.map(request => 
        request.id === selectedRequest.id 
          ? { ...request, status } 
          : request
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
    
    handleStatusClose();
  };

  const isAdmin = user?.role === 'admin';
  const canManageRequest = (request) => {
    return isAdmin || request.createdBy?.id === user?.uid;
  };

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '1200px',
      margin: '0 auto',
      p: { xs: 2, sm: 3 },
    }}>
      {/* Header */}
      <Box sx={{ 
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/app')} 
            sx={{
              color: 'text.secondary',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              '&:hover': {
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
              },
              borderRadius: 2
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ 
              fontFamily: 'StrawberrySansDisplay',
              fontWeight: 500,
              color: 'text.primary',
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}>
              Feature Requests
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Request and vote for new features
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 2,
          }}
        >
          New Request
        </Button>
      </Box>

      {/* Main Content */}
      <Paper 
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0 }}>
            {featureRequests.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 8 
              }}>
                <LightbulbOutlinedIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No feature requests yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Be the first to suggest a new feature
                </Typography>
              </Box>
            ) : (
              featureRequests.map((request, index) => (
                <React.Fragment key={request.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    alignItems="flex-start"
                    sx={{ 
                      p: 3,
                      '&:hover': {
                        bgcolor: 'background.default'
                      }
                    }}
                    secondaryAction={
                      canManageRequest(request) && (
                        <IconButton 
                          edge="end" 
                          onClick={(e) => handleActionClick(e, request)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemAvatar sx={{ mr: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <IconButton 
                          onClick={() => handleUpvote(request.id)}
                          color={(request.upvotedBy || []).includes(user?.uid || 'anonymous') ? 'primary' : 'default'}
                          sx={{ 
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 1.5
                          }}
                        >
                          {(request.upvotedBy || []).includes(user?.uid || 'anonymous') ? (
                            <ThumbUpIcon fontSize="small" />
                          ) : (
                            <ThumbUpOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                        <Typography variant="body2" fontWeight={500}>
                          {request.upvotes || 0}
                        </Typography>
                      </Box>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            {request.title}
                          </Typography>
                          {request.status && (
                            <Chip 
                              label={request.status} 
                              color={STATUS_COLORS[request.status]}
                              size="small"
                              sx={{ 
                                height: 24,
                                fontWeight: 'medium',
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                            sx={{ display: 'inline', whiteSpace: 'pre-wrap' }}
                          >
                            {request.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 1 }}>
                            <Avatar 
                              src={request.createdBy?.avatar} 
                              alt={request.createdBy?.name}
                              sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                            >
                              {request.createdBy?.name?.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" color="text.secondary">
                              {request.createdBy?.name} • {formatDistanceToNow(request.createdAt, { addSuffix: true })}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))
            )}
          </List>
        )}
      </Paper>

      {/* Feature Request Dialog (Create/Edit) */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingRequest ? 'Edit Feature Request' : 'New Feature Request'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            variant="outlined"
            value={newRequest.title}
            onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={newRequest.description}
            onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
            placeholder="Describe the feature you'd like to see"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitRequest} 
            variant="contained"
            disabled={!newRequest.title || !newRequest.description || submitting}
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
          >
            {submitting ? 'Saving...' : (editingRequest ? 'Save Changes' : 'Submit Request')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Delete Feature Request
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this feature request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setConfirmDelete(false)}
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteRequest} 
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Menu for Admins */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
          }
        }}
      >
        {statusOptions.map((status) => {
          const statusValue = typeof status === 'object' ? status.value : status;
          const statusLabel = typeof status === 'object' ? status.label : status;
          const statusColor = statusValue ? STATUS_COLORS[statusValue] : 'default';
          
          return (
            <MenuItem key={statusValue || 'empty'} onClick={() => handleStatusChange(statusValue)}>
              <Chip 
                label={statusLabel}
                color={statusColor}
                size="small"
                sx={{ 
                  height: 24,
                  fontWeight: 'medium',
                  fontSize: '0.7rem',
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Action Menu for Request Owner */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
          }
        }}
      >
        {isAdmin && selectedRequest && (
          <MenuItem onClick={(e) => {
            handleActionClose();
            handleStatusClick(e, selectedRequest);
          }}>
            <ListItemText primary="Change status" />
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          handleActionClose();
          handleOpenDialog(selectedRequest);
        }}>
          <ListItemText primary="Edit request" />
          <EditIcon fontSize="small" sx={{ ml: 1, color: 'action.active' }} />
        </MenuItem>
        <MenuItem onClick={openDeleteConfirmation}>
          <ListItemText primary="Delete request" />
          <DeleteIcon fontSize="small" sx={{ ml: 1, color: 'error.main' }} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FeatureRequestPage; 